// pfs-token-management — Personal-Access-Token-Self-Service-Verwaltung (M5-132).
//
// Das wiederverwendbare Settings-Panel, mit dem ein eingeloggter Nutzer seine
// Personal Access Tokens (PATs) verwaltet — für CLI-Tools und Dienste, die in
// seinem Namen maschinell auf token-gated APIs zugreifen.
// Zustandsautomat + Show-once-UX gespiegelt von <pfs-mfa-settings>; die Zeilen,
// Formularfelder und das Layout KOMPONIEREN die geteilten Primitive
// (<pfs-field>/<pfs-text-field>/<pfs-select-field>/<pfs-checkbox-field>/
// <pfs-empty-state>/<pfs-row>/<pfs-stack>/<pfs-badge>) statt handgerolltem Markup.
//
// Endpoints (pfs-auth, M5-132):
//   GET    {apiBase}/api/tokens        — Liste
//   POST   {apiBase}/api/tokens        — erstellen (Klartext EINMAL)
//   DELETE {apiBase}/api/tokens/{id}   — widerrufen
//
// Auth: Cookie über den Host-Proxy (apiFetch credentials:include). Der Exchange
// (PAT→kurzlebiges JWT) läuft maschinell im Client, NICHT über dieses WC.
//
// Konfiguration (data-*):
//   data-scopes            Komma-Liste anbietbarer Berechtigungen im Formular
//                          (z.B. "app.read,app.write"). Leer →
//                          freies Komma-Eingabefeld.
//   data-download-filename Dateiname für den Token-Download (Default
//                          "personal-access-token.txt").
//   data-loading-variant   Loader-Größe (Default "main").
//
// Events: pfs:token-changed (bubbles, composed) — nach Erstellen/Widerrufen.
// Texte hart (DE) — wie die MFA-WCs; i18n folgt plattformweit (PFS-418).

import { defineComponent } from "../../composition/define-component.js";
import "../badge/pfs-badge.js";
import "../loading-indicator/pfs-loading-indicator.js";
import "../card-header/pfs-card-header.js";
import "../layout/pfs-card.js";
import "../card-body/pfs-card-body.js";
import "../layout/pfs-row.js";
import "../layout/pfs-stack.js";
import "../field/pfs-field.js";
import "../text-field/pfs-text-field.js";
import "../select-field/pfs-select-field.js";
import "../checkbox-field/pfs-checkbox-field.js";
import "../empty-state/pfs-empty-state.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { apiFetch, ApiError } from "../../store/api-fetch.js";
import { confirmDialog } from "../confirm-dialog/pfs-confirm-dialog.js";

function esc(s: string): string {
  return String(s ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return s;
  }
}

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.detail ?? fallback;
  return fallback;
}

interface TokenView {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  status: string;
  createdAt: string;
  expiresAt?: string;
  lastUsedAt?: string;
}

interface CreateResult {
  token: string; // Klartext — nur genau einmal
  view: TokenView; // die erstellte Ressource (Name/Scopes/…)
}

type View =
  | { kind: "list" }
  | { kind: "create-form" }
  | { kind: "show-secret-once"; token: string; name: string };

interface TokenHost extends HTMLElement {
  _tokens?: TokenView[];
  _view?: View;
  _loading?: boolean;
  _busy?: boolean; // async Aktion läuft (Trigger gesperrt + Spinner) — Plattform-Kanon
  _error?: string;
}

// pfs-select-field/-text-field/-checkbox-field exponieren .value/.checked
// (+ .options als Property beim Select) — als schlanke Struktur-Typen getippt,
// damit wir keine internen Element-Typen importieren müssen.
type SelectFieldEl = HTMLElement & { value: string; options: { value: string; label: string }[] };
type ValueEl = HTMLElement & { value: string };
type CheckedEl = HTMLElement & { checked: boolean };

// Ablauf-Optionen im Erstellen-Formular (Tage; 0 = läuft nie ab).
const EXPIRY_OPTIONS: { label: string; days: number }[] = [
  { label: "Läuft nie ab", days: 0 },
  { label: "30 Tage", days: 30 },
  { label: "90 Tage", days: 90 },
  { label: "1 Jahr", days: 365 },
];

const ICON_KEY = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path stroke-linecap="round" stroke-linejoin="round" d="M15 7a4 4 0 10-3.9 4.9L7 16v3h3l.5-.5H13v-2h2l1.4-1.4A4 4 0 0015 7z"/></svg>`;

const CARD_SUBTITLE = "Langlebige Tokens, mit denen CLI-Tools und Dienste in deinem Namen auf APIs zugreifen.";

function slotIcon(icon: string): string {
  return icon.replace("<svg ", `<svg slot="icon" `);
}

function cardHeader(title: string, subtitle: string, icon: string, extra = ""): string {
  return `<pfs-card-header data-title="${esc(title)}"${subtitle ? ` data-subtitle="${esc(subtitle)}"` : ""}>${slotIcon(icon)}${extra}</pfs-card-header>`;
}

// Nur der schlanke Rest, den kein Primitiv abdeckt: Token-Zeile (wie die
// MFA-Faktor-Zeile, Idiom B für kurze Self-Service-Listen), Show-once-Warnung +
// Secret-Box (MFA macht es identisch), Spinner.
const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; color: var(--pfs-color-on-surface); font-size: 0.9rem; }
${BUTTON_CSS}
.muted { color: var(--pfs-color-on-surface-muted); }
.error { color: var(--pfs-color-on-danger-container); margin: 0; font-size: 0.85rem; }

.tokens { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.6rem; }
.token { display: flex; align-items: center; gap: 0.85rem; padding: 0.75rem 0.85rem; border: 1px solid var(--pfs-color-outline); border-radius: 0.8rem; background: var(--pfs-color-surface-sunken); }
.token .fi { display: grid; place-items: center; height: 40px; width: 40px; border-radius: 0.65rem; flex: none; color: var(--pfs-color-primary); background: var(--pfs-color-surface); border: 1px solid var(--pfs-color-outline); }
.token .fi svg { width: 19px; height: 19px; }
.token .fm { min-width: 0; display: flex; flex-direction: column; gap: 0.18rem; }
.token .fm .n { font-weight: 600; }
.token .fm .s { font-size: 0.78rem; color: var(--pfs-color-on-surface-subtle); font-family: ui-monospace, monospace; }
.token .fm .scopes { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.1rem; }
.token .fa { margin-left: auto; }

.scope-badge { font-size: 0.68rem; font-weight: 600; padding: 2px 7px; border-radius: 999px; color: var(--pfs-color-on-surface-muted); background: var(--pfs-color-surface); border: 1px solid var(--pfs-color-outline); font-family: ui-monospace, monospace; }

.footer { border-top: 1px solid var(--pfs-color-outline); padding-top: 0.9rem; margin-top: 0.3rem; }

/* Show-once — kein Primitiv (MFA rollt Warnung + Secret-Box identisch, pfs-mfa-settings). */
.warn { display: flex; gap: 0.6rem; align-items: flex-start; font-size: 0.82rem; color: var(--pfs-color-badge-fg); background: var(--pfs-color-badge-bg); border: 1px solid color-mix(in srgb, var(--pfs-color-badge-fg) 25%, transparent); border-radius: 0.7rem; padding: 0.7rem 0.85rem; }
.secretbox { font-family: ui-monospace, monospace; word-break: break-all; font-size: 0.9rem; background: var(--pfs-color-surface-sunken); border: 1px solid var(--pfs-color-outline); border-radius: 0.6rem; padding: 0.75rem 0.85rem; margin: 0.9rem 0; }
.copied { align-self: center; font-size: 0.8rem; font-weight: 600; color: var(--pfs-color-on-success-container); }
.spin { display:inline-block; width:0.9em; height:0.9em; margin-right:0.45em; vertical-align:-0.1em; border:2px solid currentColor; border-top-color:transparent; border-radius:50%; animation: pfs-spin 0.7s linear infinite; }
@keyframes pfs-spin { to { transform: rotate(360deg); } }
`;

function availableScopes(host: HTMLElement): string[] {
  const raw = host.getAttribute("data-scopes");
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

defineComponent({
  tagName: "pfs-token-management",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const h = host as TokenHost;
    if (!h._view) h._view = { kind: "list" };

    const scopeCatalog = availableScopes(host);
    const loadingVariant = host.getAttribute("data-loading-variant") || "main";

    const setState = (
      patch: Partial<Pick<TokenHost, "_tokens" | "_view" | "_loading" | "_busy" | "_error">>,
    ): void => {
      Object.assign(h, patch);
      paint();
    };

    const emitChanged = (): void => {
      host.dispatchEvent(new CustomEvent("pfs:token-changed", { bubbles: true, composed: true }));
    };

    const loadTokens = async (): Promise<void> => {
      setState({ _loading: true, _error: undefined });
      try {
        const res = await apiFetch<{ tokens: TokenView[] }>(`/api/tokens`);
        setState({ _tokens: res.tokens ?? [], _loading: false, _busy: false });
      } catch (e) {
        setState({ _loading: false, _busy: false, _error: errMsg(e, "Tokens konnten nicht geladen werden.") });
      }
    };

    const createToken = async (name: string, scopes: string[], expiryDays: number): Promise<void> => {
      setState({ _busy: true, _error: undefined });
      const body: Record<string, unknown> = { name, scopes };
      if (expiryDays > 0) {
        body.expiresAt = new Date(Date.now() + expiryDays * 86400_000).toISOString();
      }
      try {
        const res = await apiFetch<CreateResult>(`/api/tokens`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        h._busy = false;
        // Der Response führt Klartext + die erstellte Ressource (view). Der
        // Anzeigename kommt aus der View; lokaler Name als robuster Fallback.
        h._view = { kind: "show-secret-once", token: res.token, name: res.view?.name ?? name };
        paint();
        emitChanged();
      } catch (e) {
        setState({ _busy: false, _error: errMsg(e, "Token konnte nicht erstellt werden.") });
      }
    };

    const revokeToken = async (t: TokenView): Promise<void> => {
      const ok = await confirmDialog({
        title: "Token widerrufen?",
        message: `Das Token „${t.name}" wird sofort ungültig. Dienste, die es nutzen, verlieren binnen weniger Minuten den Zugriff.`,
        confirmLabel: "Widerrufen",
        variant: "danger",
      });
      if (!ok) return;
      setState({ _busy: true, _error: undefined });
      try {
        await apiFetch(`/api/tokens/${encodeURIComponent(t.id)}`, { method: "DELETE" });
        emitChanged();
        await loadTokens();
      } catch (e) {
        // Idempotent: 404 = bereits weg → Zielzustand erreicht, kein Fehler.
        if (e instanceof ApiError && e.status === 404) {
          emitChanged();
          await loadTokens();
          return;
        }
        setState({ _busy: false, _error: errMsg(e, "Widerrufen fehlgeschlagen.") });
      }
    };

    // Hard-Delete (Purge) — nur auf bereits widerrufenen Tokens (Backend gated
    // mit 409). Räumt den Tombstone endgültig weg (Soft/Hard-Delete-Kanon).
    const purgeToken = async (t: TokenView): Promise<void> => {
      const ok = await confirmDialog({
        title: "Token endgültig löschen?",
        message: `Das widerrufene Token „${t.name}" wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.`,
        confirmLabel: "Löschen",
        variant: "danger",
      });
      if (!ok) return;
      setState({ _busy: true, _error: undefined });
      try {
        await apiFetch(`/api/tokens/${encodeURIComponent(t.id)}?hard=true`, { method: "DELETE" });
        emitChanged();
        await loadTokens();
      } catch (e) {
        // Idempotent: 404 = bereits weg → Zielzustand erreicht, kein Fehler.
        if (e instanceof ApiError && e.status === 404) {
          emitChanged();
          await loadTokens();
          return;
        }
        setState({ _busy: false, _error: errMsg(e, "Löschen fehlgeschlagen.") });
      }
    };

    function tokenRow(t: TokenView): string {
      const revoked = t.status === "revoked";
      const badgeVariant = revoked ? "neutral" : "success";
      const statusLabel = revoked ? "Widerrufen" : "Aktiv";
      const used = t.lastUsedAt ? `zuletzt genutzt ${esc(fmtDate(t.lastUsedAt))}` : `erstellt ${esc(fmtDate(t.createdAt))}`;
      const expiry = t.expiresAt ? ` · gültig bis ${esc(fmtDate(t.expiresAt))}` : "";
      const scopes = (t.scopes ?? []).length
        ? `<span class="scopes">${(t.scopes ?? []).map((s) => `<span class="scope-badge">${esc(s)}</span>`).join("")}</span>`
        : "";
      const action = revoked
        ? `<button class="btn danger sm" data-purge="${esc(t.id)}" ${h._busy ? "disabled" : ""}>Löschen</button>`
        : `<button class="btn danger sm" data-revoke="${esc(t.id)}" ${h._busy ? "disabled" : ""}>Widerrufen</button>`;
      return `
        <li class="token">
          <span class="fi">${ICON_KEY}</span>
          <span class="fm">
            <span class="n">${esc(t.name)}</span>
            <span class="s">${esc(t.prefix)}… · ${used}${expiry}</span>
            ${scopes}
          </span>
          <span class="fa"><pfs-row data-gap="action" data-valign="center"><pfs-badge data-variant="${badgeVariant}">${esc(statusLabel)}</pfs-badge>${action}</pfs-row></span>
        </li>`;
    }

    function listView(): string {
      if (h._loading) {
        return `
          ${cardHeader("Personal Access Tokens", CARD_SUBTITLE, ICON_KEY)}
          <pfs-card-body><pfs-loading-indicator data-variant="${loadingVariant}" data-text="Tokens werden geladen"></pfs-loading-indicator></pfs-card-body>`;
      }
      const tokens = h._tokens ?? [];
      const active = tokens.filter((t) => t.status !== "revoked");
      const countBadge = active.length
        ? `<span slot="header-right"><pfs-badge data-variant="success">${active.length} aktiv</pfs-badge></span>`
        : "";
      const list = tokens.length
        ? `<ul class="tokens">${tokens.map(tokenRow).join("")}</ul>`
        : `<pfs-empty-state data-variant="plain" data-title="Noch kein Token erstellt."></pfs-empty-state>`;
      return `
        ${cardHeader("Personal Access Tokens", CARD_SUBTITLE, ICON_KEY, countBadge)}
        <pfs-card-body>
          <pfs-stack data-gap="field">
            ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
            ${list}
            <pfs-row class="footer" data-gap="action"><button class="btn success" data-new ${h._busy ? "disabled" : ""}>Token erstellen</button></pfs-row>
          </pfs-stack>
        </pfs-card-body>`;
    }

    function createView(): string {
      const scopeField = scopeCatalog.length
        ? scopeCatalog
            .map((s) => `<pfs-checkbox-field data-scope-key="${esc(s)}" data-label="${esc(s)}" data-checked></pfs-checkbox-field>`)
            .join("")
        : `<pfs-field data-label="Berechtigungen" data-hint="Kommagetrennte Berechtigungen (&lt;app&gt;.&lt;permission&gt;).">
             <pfs-text-field data-name="scopes-text" data-placeholder="z. B. app.read, app.write"></pfs-text-field>
           </pfs-field>`;
      const scopeBlock = scopeCatalog.length
        ? `<pfs-field data-label="Berechtigungen"><pfs-stack data-gap="label">${scopeField}</pfs-stack></pfs-field>`
        : scopeField;
      return `
        ${cardHeader("Token erstellen", "Der Klartext wird nur genau einmal angezeigt.", ICON_KEY)}
        <pfs-card-body>
          <pfs-stack data-gap="field">
            <pfs-field data-label="Name" data-required data-hint="Ein Name, an dem du das Token wiedererkennst.">
              <pfs-text-field data-name="name" data-placeholder="z. B. mein-laptop"></pfs-text-field>
            </pfs-field>
            ${scopeBlock}
            <pfs-field data-label="Gültigkeit">
              <pfs-select-field data-name="expiry"></pfs-select-field>
            </pfs-field>
            ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
            <pfs-row data-gap="action">
              <button class="btn secondary" data-cancel ${h._busy ? "disabled" : ""}>Abbrechen</button>
              <button class="btn success" data-do-create ${h._busy ? "disabled" : ""}>${h._busy ? `<span class="spin" aria-hidden="true"></span>Erstelle…` : "Token erstellen"}</button>
            </pfs-row>
          </pfs-stack>
        </pfs-card-body>`;
    }

    function showSecretView(token: string, name: string): string {
      return `
        ${cardHeader("Token erstellt", "Jetzt kopieren — danach nie wieder sichtbar.", ICON_KEY)}
        <pfs-card-body>
          <pfs-stack data-gap="field">
            <div class="warn"><span aria-hidden="true">⚠</span><span>Dieses Token wird nur JETZT angezeigt. Kopiere es und hinterlege es sicher (z. B. in der Client-Konfiguration). Danach kann es nicht erneut abgerufen werden.</span></div>
            <div class="secretbox" data-secret>${esc(token)}</div>
            <pfs-row data-gap="action" data-valign="center">
              <button class="btn secondary" data-download>Herunterladen</button>
              <button class="btn secondary" data-copy>Kopieren</button>
              <span class="copied" data-copied hidden>Kopiert ✓</span>
            </pfs-row>
            <pfs-checkbox-field data-ack-check data-label='Ich habe das Token „${esc(name)}" sicher gespeichert.'></pfs-checkbox-field>
            <pfs-row data-gap="action"><button class="btn primary" data-ack disabled>Fertig</button></pfs-row>
          </pfs-stack>
        </pfs-card-body>`;
    }

    function paint(): void {
      if (!root) return;
      const view = h._view ?? { kind: "list" };
      let inner = "";
      if (view.kind === "create-form") inner = createView();
      else if (view.kind === "show-secret-once") inner = showSecretView(view.token, view.name);
      else inner = listView();
      root.innerHTML = `<style>${STYLES}</style><pfs-card data-sectioned>${inner}</pfs-card>`;
      // Select-Optionen sind eine Property (kein data-attr) → nach dem Render setzen.
      const expiry = root.querySelector<SelectFieldEl>("[data-name='expiry']");
      if (expiry) {
        expiry.options = EXPIRY_OPTIONS.map((o) => ({ value: String(o.days), label: o.label }));
        expiry.value = "0";
      }
      wire();
    }

    function selectedScopes(): string[] {
      if (!root) return [];
      if (scopeCatalog.length) {
        return Array.from(root.querySelectorAll<CheckedEl>("[data-scope-key]"))
          .filter((c) => c.checked)
          .map((c) => c.getAttribute("data-scope-key") ?? "")
          .filter(Boolean);
      }
      const text = root.querySelector<ValueEl>("[data-name='scopes-text']")?.value ?? "";
      return text.split(",").map((s) => s.trim()).filter(Boolean);
    }

    function wire(): void {
      if (!root) return;
      root.querySelector("[data-new]")?.addEventListener("click", () => {
        h._view = { kind: "create-form" };
        h._error = undefined;
        paint();
      });
      root.querySelectorAll<HTMLElement>("[data-revoke]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.getAttribute("data-revoke") ?? "";
          const t = (h._tokens ?? []).find((x) => x.id === id);
          if (t) void revokeToken(t);
        }),
      );
      root.querySelectorAll<HTMLElement>("[data-purge]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.getAttribute("data-purge") ?? "";
          const t = (h._tokens ?? []).find((x) => x.id === id);
          if (t) void purgeToken(t);
        }),
      );
      root.querySelector("[data-cancel]")?.addEventListener("click", () => {
        h._view = { kind: "list" };
        h._error = undefined;
        paint();
      });
      const doCreate = (): void => {
        const name = (root.querySelector<ValueEl>("[data-name='name']")?.value ?? "").trim();
        if (!name) {
          setState({ _error: "Bitte einen Namen angeben." });
          return;
        }
        const days = Number(root.querySelector<SelectFieldEl>("[data-name='expiry']")?.value ?? "0");
        void createToken(name, selectedScopes(), Number.isFinite(days) ? days : 0);
      };
      root.querySelector("[data-do-create]")?.addEventListener("click", doCreate);
      // Enter im Namensfeld = erstellen (keydown ist composed → quert die Shadow-Grenze).
      root.querySelector("[data-name='name']")?.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter") doCreate();
      });

      // Show-once: Download / Kopieren + Bestätigungs-Gate.
      const secretText = (): string => {
        const v = h._view;
        return v && v.kind === "show-secret-once" ? v.token : "";
      };
      root.querySelector("[data-download]")?.addEventListener("click", () => {
        const blob = new Blob([secretText() + "\n"], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = host.getAttribute("data-download-filename") || "personal-access-token.txt";
        a.click();
        URL.revokeObjectURL(url);
      });
      root.querySelector("[data-copy]")?.addEventListener("click", () => {
        void navigator.clipboard?.writeText(secretText());
        const c = root.querySelector<HTMLElement>("[data-copied]");
        if (c) c.hidden = false;
      });
      const ackCheck = root.querySelector<CheckedEl>("[data-ack-check]");
      const ackBtn = root.querySelector<HTMLButtonElement>("[data-ack]");
      ackCheck?.addEventListener("change", () => {
        if (ackBtn) ackBtn.disabled = !ackCheck.checked;
      });
      root.querySelector("[data-ack]")?.addEventListener("click", () => {
        h._view = { kind: "list" };
        void loadTokens();
      });
    }

    paint();
    void loadTokens();
  },
});
